import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { UserInvitation } from '../data-models/user-invitation.model';
import { User } from '../data-models/user.model';
import { UserRequest } from '../request-models/user-request.model';
import { AdminRoleResult } from '../response-models/admin-role-result.model';
import { Coordinates } from '../response-models/coordinates.model';
import { Page } from '../response-models/page.model';
import { UserClaimMembership } from '../response-models/user-claim-membership.model';
import { UserClaims } from '../response-models/user-claims.model';
import { UserLockStatus } from '../response-models/user-lock-status.model';
import { MembershipPlan } from '../data-models/membership-plan.model';
import { toCoordinates, toCoordinatesRequest } from '../utils/coordinates-utils';
import { isRecord } from '../utils/is-record';
import { toUserRequest } from '../utils/user-request-utils';
import { countOrZero, idString, nullableString, stringArrayOrEmpty } from '../utils/wire-value-utils';

/** Query for the users list. Blank values are not sent. */
export interface UserListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  emailVerified?: string;
  is2faEnabled?: string;
  membershipPlan?: string;
}

/**
 * ADN users (profiles): `/admin/users` (hub `admin-users-controller.ts`), paged with `pageSize`. Also the
 * user-detail actions under `/admin/users/:id/...` (claims, invitations, lock status, verification, admin role).
 * `setAdminRole` is super-admin only on the hub.
 */
@Injectable({ providedIn: 'root' })
export class UserService extends BaseService<User> {
  constructor() {
    super('admin/users', User);
  }

  /** GET /admin/users: one page of users (list fields only). */
  getUsers(query: UserListQuery = {}): Observable<Page<User>> {
    return this.getPage({ query: toListQuery(query) });
  }

  /** Returns the user associated with the userId, or null if absent. */
  getUser(userId: string): Observable<User | null> {
    return this.get(userId);
  }

  /** PUT /admin/users/:id with only the writable fields that are set. */
  updateUser(userId: string, fields: UserRequest): Observable<User | null> {
    return this.update(userId, toUserRequest(fields));
  }

  /**
   * Sets or clears the reset requirement. The Hub owns the accompanying timestamp — it's what the token gate
   * compares Firebase's passwordUpdatedAt against, so a client-supplied value could backdate past the check.
   */
  requirePasswordReset(userId: string, requiresPasswordReset: boolean): Observable<User | null> {
    return this.updateUser(userId, { requiresPasswordReset });
  }

  /** GET /admin/users/:id/claims: global roles and organization memberships from the Firebase claims. */
  getClaims(userId: string): Observable<UserClaims> {
    return this.request('GET', { id: userId, path: 'claims' }, toClaims);
  }

  /** GET /admin/users/:id/invitations: profile and organization invitations, newest first. */
  getInvitations(userId: string): Observable<UserInvitation[]> {
    return this.request('GET', { id: userId, path: 'invitations' }, data =>
      Array.isArray(data) ? data.map(item => new UserInvitation(item)) : []
    );
  }

  /** GET /admin/users/:id/lock-status: the hub sends the fields directly as `data`. */
  getLockStatus(userId: string): Observable<UserLockStatus> {
    return this.request('GET', { id: userId, path: 'lock-status' }, toLockStatus);
  }

  /** POST /admin/users/:id/lock-account or /unlock-account. */
  setLocked(userId: string, locked: boolean): Observable<void> {
    return this.action(userId, locked ? 'lock-account' : 'unlock-account', {});
  }

  /** POST /admin/users/:id/toggle-email-verification */
  setEmailVerified(userId: string, emailVerified: boolean): Observable<void> {
    return this.action(userId, 'toggle-email-verification', { emailVerified });
  }

  /** POST /admin/users/:id/resend-verification */
  resendVerification(userId: string): Observable<void> {
    return this.action(userId, 'resend-verification', {});
  }

  /** POST /admin/users/:id/send-reset-password */
  sendResetPassword(userId: string): Observable<void> {
    return this.action(userId, 'send-reset-password', {});
  }

  /** POST /admin/users/:id/admin-role (super admin only). */
  setAdminRole(userId: string, isAdmin: boolean): Observable<AdminRoleResult> {
    return this.request('POST', { id: userId, path: 'admin-role' }, toAdminRoleResult, { isAdmin });
  }

  /** GET /admin/users/:id/geocode: coordinates for the saved address, or null if the hub sent none. */
  geocode(userId: string): Observable<Coordinates | null> {
    return this.request('GET', { id: userId, path: 'geocode' }, toCoordinates);
  }

  /** PUT /admin/users/:id/coordinates */
  updateCoordinates(userId: string, coords: Coordinates): Observable<void> {
    return this.request('PUT', { id: userId, path: 'coordinates' }, () => undefined, toCoordinatesRequest(coords));
  }

  private action(userId: string, path: string, body: object): Observable<void> {
    return this.request('POST', { id: userId, path }, () => undefined, body);
  }
}

function toListQuery(query: UserListQuery): Record<string, string | number | undefined> {
  return {
    page: query.page,
    pageSize: query.pageSize,
    search: query.search?.trim() || undefined,
    sortBy: query.sortBy || undefined,
    sortOrder: query.sortOrder,
    emailVerified: query.emailVerified || undefined,
    is2faEnabled: query.is2faEnabled || undefined,
    membershipPlan: query.membershipPlan || undefined,
  };
}

function toClaims(data: unknown): UserClaims {
  const source = isRecord(data) ? data : {};
  const memberships = Array.isArray(source['memberships']) ? source['memberships'].map(toClaimMembership) : [];
  return {
    globalRoles: stringArrayOrEmpty(source['globalRoles']),
    isAdmin: source['isAdmin'] === true,
    isSuperAdmin: source['isSuperAdmin'] === true,
    memberships,
  };
}

function toClaimMembership(item: unknown): UserClaimMembership {
  const source = isRecord(item) ? item : {};
  return {
    organizationId: idString(source['organizationId']),
    organizationName: nullableString(source['organizationName']) ?? '',
    roles: stringArrayOrEmpty(source['roles']),
    membershipPlan: (nullableString(source['membershipPlan']) ?? null) as MembershipPlan | null,
  };
}

function toLockStatus(data: unknown): UserLockStatus {
  const source = isRecord(data) ? data : {};
  return {
    isLocked: source['isLocked'] === true,
    firebaseDisabled: source['firebaseDisabled'] === true,
    isAutoLocked: source['isAutoLocked'] === true,
    accountLockedUntil: nullableString(source['accountLockedUntil']) ?? null,
    failedLoginAttempts: countOrZero(source['failedLoginAttempts']),
  };
}

function toAdminRoleResult(data: unknown): AdminRoleResult {
  const source = isRecord(data) ? data : {};
  return { isAdmin: source['isAdmin'] === true, isSuperAdmin: source['isSuperAdmin'] === true };
}
